import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject } from "@/shared/utils/typeGuards";

export const kTelemetryRoute = "/telemetry";

export type TelemetryKind = "feedback" | "event";

/**
 * Which server wrote a record: the deployed Lambda, or anything else.
 *
 * Amplitude already keeps the two apart by writing to different projects, but
 * S3 has one bucket and a dev server holding `TELEMETRY_BUCKET_NAME` writes to
 * it too — so without this, local development and real usage are the same rows
 * with nothing to tell them apart.
 */
export type TelemetryEnvironment = "production" | "development";
export type TelemetryDisplayMode = "web" | "compact";
export type TelemetrySurface = "web" | "creative-partner" | "labs-plugin";

export const kTelemetrySurfaces: readonly TelemetrySurface[] = [
    "web",
    "creative-partner",
    "labs-plugin",
];
export type TelemetryProps = Record<string, string | number | boolean>;

export interface TelemetryRequestBody {
    kind: TelemetryKind;
    /** App directory (e.g. "splice/craft") or "home". */
    app: string;
    /**
     * Event name; required when kind === "event". A `TelemetryEventName` from
     * `shared/telemetry-events.api.ts` in practice — the client type enforces
     * that, and this stays a plain string on purpose. Clients are deployed
     * statics that a user can hold for weeks, so a name retired today would
     * otherwise start returning 400s to a browser still running last month's
     * build. Constrain the developer at compile time; accept what arrives.
     */
    name?: string;
    /** 1-5; feedback only. */
    rating?: number;
    /** Feedback only. */
    comment?: string;
    props?: TelemetryProps;
    displayMode?: TelemetryDisplayMode;
    /** Hosting surface: web site, Creative Partner sidepanel, or Labs plugin. */
    surface?: TelemetrySurface;
    /** Opaque audience label, e.g. "internal,soundcheck". */
    audience?: string;
    /** Client build commit (VITE_GIT_COMMIT). */
    commit?: string;
}

/** What the server persists: the request plus server-stamped fields. */
export interface TelemetryRecord extends TelemetryRequestBody {
    /** Splice user sub from the verified JWT. */
    sub?: string;
    /**
     * The bare Splice user UUID, from the verified JWT's
     * `https://splice.com/user_uuid` claim. This is the same value Soundcheck
     * stores as `splice_uuid` and LaunchDarkly keys its contexts on, so it is
     * what joins "applied to Soundcheck" to "used this Labs tool".
     *
     * Server-stamped, never sent by the client: a client-supplied user id is a
     * client-supplied claim about who someone is.
     */
    userUuid?: string;
    /** ISO-8601, server clock. */
    receivedAt: string;
    userAgent?: string;
    /**
     * Stamped from `isDeployedLambda()`, never from the request: `surface` and
     * `app` are claims the page makes about itself, so routing on either would
     * let one embedded host we do not control file its traffic as production.
     *
     * Optional because records written before 2026-09-25 do not carry it. A
     * report that wants production only should require the value rather than
     * treat "missing" as production.
     */
    environment?: TelemetryEnvironment;
}

export interface TelemetrySuccessResponse {
    ok: true;
}

const isTelemetryProps = (value: unknown): value is TelemetryProps =>
    isObject(value) &&
    Object.values(value).every(
        (v) => typeof v === "string" || typeof v === "number" || typeof v === "boolean",
    );

export const isTelemetryRequestBody = (value: unknown): value is TelemetryRequestBody =>
    isObject(value) &&
    (value.kind === "feedback" || value.kind === "event") &&
    typeof value.app === "string" &&
    value.app.length > 0 &&
    (value.kind !== "event" || (typeof value.name === "string" && value.name.length > 0)) &&
    (value.name === undefined || typeof value.name === "string") &&
    (value.rating === undefined ||
        (typeof value.rating === "number" && value.rating >= 1 && value.rating <= 5)) &&
    (value.comment === undefined || typeof value.comment === "string") &&
    (value.kind !== "feedback" || value.rating !== undefined || value.comment !== undefined) &&
    (value.props === undefined || isTelemetryProps(value.props)) &&
    (value.displayMode === undefined ||
        value.displayMode === "web" ||
        value.displayMode === "compact") &&
    (value.surface === undefined ||
        (typeof value.surface === "string" &&
            (kTelemetrySurfaces as readonly string[]).includes(value.surface))) &&
    (value.audience === undefined || typeof value.audience === "string") &&
    (value.commit === undefined || typeof value.commit === "string");

export const isTelemetrySuccessResponse = (value: unknown): value is TelemetrySuccessResponse =>
    isObject(value) && value.ok === true;

export const kTelemetryEndpoint: ApiEndpoint<TelemetryRequestBody, TelemetrySuccessResponse> = {
    route: kTelemetryRoute,
    label: "Telemetry",
    source: "Backstage",
    category: "Platform",
    description: "Receives user feedback and minimal usage events from Backstage clients.",
    isRequestBody: isTelemetryRequestBody,
    isSuccessResponse: isTelemetrySuccessResponse,
};
